import { documentCategoryApi, documentItemApi } from "apiClient/v2";
import { message } from "components/base";
import { DocumentCategoryKey, ModalType } from "constants/enum";
import { DocumentCategoryDTO } from "interfaces/dtos/documentCategoryDTO";
import { DocumentItemDTO } from "interfaces/dtos/documentItemDTO";
import { removeDocItem } from "models/documentItem";
import { useForgeViewerContext } from "pages/forge-viewer/ForgeViewerContext";
import { TypeHandleInitData } from "pages/forge-viewer/hooks/useSupportSyncDataOffline";
import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  removeDocumentItems,
  setDocumentCategorySelected,
  setDocumentItemSelected,
  updateDocumentCategories,
} from "redux/documentSlice";
import { setModalType } from "redux/forgeViewerSlice";
import store from "redux/store";
import { updateTaskByDocumentItemId } from "redux/taskSlice";
import { updateForgeWhenSelectCategory } from "utils/document";
import { selectDbIds } from "utils/forge";
import { selectLabel } from "utils/forge/extensions/custom-label";
import { clearLabelAndForgeView } from "utils/forge/extensions/custom-label/index";
import { logDev } from "utils/logs";
import { arrayToObject } from "utils/object";
import { transformBodyForCombineData } from "utils/offline";

type UseDeletedDocumentItemReturnType = {
  onDeletedDocumentItem: (
    documentItem: DocumentItemDTO,
    onOk?: (documentCategory?: DocumentCategoryDTO) => void
  ) => Promise<void>;
};

interface Props {
  documentCategories?: DocumentCategoryDTO[];
}

const useDeletedDocumentItem = (
  props: Props
): UseDeletedDocumentItemReturnType => {
  const { documentCategories: filteredDocumentCategories } = props;
  const { socket } = useForgeViewerContext();
  const filteredDocumentCategoriesRef = useRef<
    DocumentCategoryDTO[] | undefined
  >();

  const dispatch = useDispatch();

  filteredDocumentCategoriesRef.current = filteredDocumentCategories;

  const onDeletedDocumentItem = useCallback(
    async (
      documentItem: DocumentItemDTO,
      onOk?: (documentCategory?: DocumentCategoryDTO) => void
    ) => {
      if (!documentItem) {
        logDev("_DELETE_DOCUMENT_ITEM", documentItem);

        return;
      }
      const state = store.getState().document;
      const documentCategories = state.documentCategories;
      const documentCategory = documentCategories.find(
        (item) => item.id === documentItem.documentCategoryId
      );
      const documentItemSelected = state.documentItemSelected;
      const documentCategorySelected = state.documentCategorySelected;

      const hasDocumentSelected =
        documentCategorySelected || documentItemSelected;
      if (!documentCategory) {
        logDev("_DELETE_DOCUMENT_ITEM", documentCategory);

        return;
      }
      const documentItemId = documentItem.id;
      // setLoading(true);
      try {
        const documentCategoryId = documentItem.documentCategoryId;
        const promises: Promise<any>[] = [
          documentItemApi.deleteItemList([documentItemId]),
        ];

        const transformDocumentCategory = (
          documentCategory: DocumentCategoryDTO
        ) => {
          const documentItems = [] as DocumentItemDTO[];
          const itemIds = [] as string[];
          let order: number = 0;

          documentCategory.documentItems?.forEach((documentItem) => {
            if (documentItem.id != documentItemId) {
              documentItems.push({
                ...documentItem,
                displayOrder: order,
              });
              order++;
              itemIds.push(documentItem.id);
            }
          });

          const cloneDocumentCategory = structuredClone(documentCategory);
          cloneDocumentCategory.documentItems = documentItems;
          cloneDocumentCategory.itemIds = itemIds;
          cloneDocumentCategory.selectedExternalIds =
            cloneDocumentCategory.selectedExternalIds?.filter(
              (id) => id !== documentItemId
            );

          return cloneDocumentCategory;
        };
        const newDocumentCategory = transformDocumentCategory(documentCategory);
        promises.push(
          documentCategoryApi.updateCategory(
            transformBodyForCombineData<DocumentCategoryDTO>({
              body: {
                id: documentCategoryId!,
                selectedExternalIds: newDocumentCategory.selectedExternalIds,
                itemIds: newDocumentCategory.itemIds,
              } as DocumentCategoryDTO,
              bodyBefore: documentCategory,
              typeInitData: TypeHandleInitData.DOCUMENT_CATEGORY,
            })
          )
        );

        // Update document categories related to templates and document items
        const otherDocumentCategories = documentCategories?.filter(
          (category) =>
            category?.itemIds?.includes(documentItemId) &&
            category.templateId === newDocumentCategory.templateId &&
            category.level === newDocumentCategory.level &&
            category.id !== documentCategoryId!
        );
        const mapOtherDocumentCategoriesById = arrayToObject(
          otherDocumentCategories,
          DocumentCategoryKey.ID
        );
        const tranformOtherDocumentCategories = otherDocumentCategories.map(
          transformDocumentCategory
        );
        const now = new Date();
        promises.push(
          ...tranformOtherDocumentCategories.map((category) => {
            return documentCategoryApi.updateCategory(
              transformBodyForCombineData<DocumentCategoryDTO>({
                body: {
                  id: category.id,
                  selectedExternalIds: category.selectedExternalIds,
                  itemIds: category.itemIds,
                  updatedAt: now,
                } as DocumentCategoryDTO,
                bodyBefore: mapOtherDocumentCategoriesById[category.id],
                typeInitData: TypeHandleInitData.DOCUMENT_CATEGORY,
              })
            );
          })
        );
        await Promise.all(promises);

        const updateTaskByDocumentItemIdBody = {
          documentItemIds: [documentItemId],
          data: { isDeleteDocItem: true, updatedAt: now },
        };

        dispatch(updateTaskByDocumentItemId(updateTaskByDocumentItemIdBody));

        socket.updateTaskByDocumentItem(newDocumentCategory.level!, [
          {
            id: documentItemId,
            isDeleteDocItem: true,
            updatedAt: now,
          },
        ]);

        const categories: DocumentCategoryDTO[] = removeDocItem(
          documentCategories,
          [documentItemId]
        );

        dispatch(updateDocumentCategories(categories));
        dispatch(removeDocumentItems([documentItemId]));
        socket.deleteDocItems(newDocumentCategory.level!, [documentItemId]);
        if (documentItem.title && (documentItem.title ?? "").trim()) {
          message.success([
            "ピンの削除",
            `ピン「${documentItem.title}」を削除しました。`,
          ]);
        } else {
          message.success(["ピンの削除", `ピンを削除しました。`]);
        }
        if (hasDocumentSelected) {
          selectLabel([]);
          // clear selection in forge viewer
          selectDbIds([], {});
        }
        if (hasDocumentSelected) {
          let category = filteredDocumentCategoriesRef.current?.find(
            (category) => category.id === newDocumentCategory.id
          );
          const documentItems = category?.documentItems?.filter(
            (documentItem) => documentItem.id !== documentItemId
          );
          category = { ...category!, documentItems };
          clearLabelAndForgeView();
          // we always need delete item selected because when have filter condition, item selected can hidden -> we need change to category
          dispatch(setDocumentItemSelected());
          dispatch(setModalType(ModalType.DOC_CATEGORY));
          dispatch(setDocumentCategorySelected(category));
          // update forge viewer when select category
          //( when has only doc category selected
          //-> we need update doc category,
          // but when has doc item selected we can skip this because at hooks forge viewer has use effect update )
          if (!documentItemSelected) {
            updateForgeWhenSelectCategory(category);
          }
        }
        onOk?.(newDocumentCategory);
      } catch (error) {
        logDev("🚀 ~ onHandleDeletedDocumentItem ~ error:", error);
      } finally {
        // setLoading(false);
      }
    },
    [dispatch, socket]
  );

  return {
    onDeletedDocumentItem,
  };
};

export default useDeletedDocumentItem;
